import { useEffect } from 'react';
import { Box, Flex, Link, Text } from '@backstage/ui';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';

const STEPS = [
  {
    title: 'Set up Launchpad, once',
    body: (
      <>
        It is how your machine proves who you are to everything else — the
        catalog, the deploy pipeline, the clouds. One command, and you never
        think about it again.{' '}
        <Link href="https://launchpad.answerrocket.com/getting-started">
          Getting started
        </Link>
      </>
    ),
  },
  {
    title: 'Build it with Claude, and let the rails steer',
    body: (
      <>
        The <code>codeops</code> plugins already know our architecture, our
        stack and our standards, and they are installed on your machine whether
        or not you asked. Describe what you want; they will push back when it
        drifts off the paved road. You do not need to have read the standards
        for them to apply. <Link href="/marketplace">What is installed</Link>
        {' · '}
        <Link href="https://wiki.answerrocket.com/docs/engineering/standards/building-and-deploying">
          Building &amp; deploying
        </Link>
      </>
    ),
  },
  {
    title: 'Push it to GitHub, under answerrocket',
    body: (
      <>
        The portal can only deploy something it can see. Any repository in the{' '}
        <Link href="https://github.com/answerrocket">answerrocket</Link>{' '}
        organisation counts — it does not need to be finished, and it does not
        need to be tidy. The next step will tell you what it is missing and
        offer to add it for you.
      </>
    ),
  },
];

/**
 * The first step of the create flow, and deliberately not a form.
 *
 * # Why a wall of text is the right control here
 *
 * The portal's audience is everyone, including people who will never open a
 * terminal, and the honest truth is that three things have to be true before
 * any of the rest of this wizard can help them. Two of the three are not
 * machine-checkable from here, and the third is checked by construction in the
 * next step — so a validated checkbox would be theatre.
 *
 * What it is instead is the answer to "I have an idea, what now?", which is the
 * question the portal was previously silent on. Somebody who has already done
 * all three reads it in four seconds and moves on; somebody who has done none of
 * them would otherwise have got as far as the repository picker, found it empty,
 * and left.
 *
 * It renders nothing into the form data and satisfies itself immediately, so it
 * never blocks.
 */
export const GettingStarted = (props: FieldExtensionComponentProps<string>) => {
  const { onChange } = props;

  useEffect(() => {
    onChange('acknowledged');
    // Once on mount. `onChange` is not referentially stable in RJSF, so
    // depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Text as="p" variant="body-medium" style={{ marginBottom: 18 }}>
        This page turns an app you have already built into an app that runs,
        with a domain, a database if it needs one, CI/CD, and an entry in the
        catalog so the rest of the company can find it. Before it can, three
        things have to be true.
      </Text>

      {STEPS.map((step, index) => (
        <Flex
          key={step.title}
          gap="3"
          align="start"
          style={{ marginBottom: 16 }}
        >
          {/* A number rather than a tick: nothing here is verified, and a green
              check on an unverified claim is a small lie the user will find out
              about two steps later. */}
          <Box
            style={{
              flexShrink: 0,
              width: 22,
              height: 22,
              borderRadius: 999,
              border: '1px solid var(--bui-border-1)',
              display: 'grid',
              placeItems: 'center',
              fontFamily: 'var(--ar-font-mono)',
              fontSize: '0.75rem',
              color: 'var(--bui-fg-secondary)',
            }}
          >
            {index + 1}
          </Box>
          <Box>
            <Text as="p" weight="bold">
              {step.title}
            </Text>
            <Text as="p" variant="body-small" color="secondary">
              {step.body}
            </Text>
          </Box>
        </Flex>
      ))}

      <Text as="p" variant="body-small" color="secondary">
        Nothing you do in this wizard touches a cloud. It gathers what you want,
        opens the pull requests, and waits for a DevOps approval — so there is
        no way to break anything by clicking through it.
      </Text>
    </Box>
  );
};
